/**
 * LocationAdminTable — one-to-one copy of the .lm-admin-table design
 * from location_management_Preview16.html.
 *
 * Design classes reproduced:
 *   .lm-admin-table-scroll  .lm-admin-table  .lm-group-row  .lm-group-button
 *   .lm-group-total  .lm-location-name  .lm-location-sub  .lm-table-status
 *   .lm-status-wrap  .lm-status-action  .lm-status-menu  .lm-status-dot
 *   .lm-icon-btn  .lm-row-check  .lm-table-footer  select-col
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  Platform,
  ActivityIndicator,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import Svg, { Path } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import { Location } from '../locationTypes';
import { useTheme } from '../../../../../theme/useTheme';

const createPortal = Platform.OS === 'web' ? require('react-dom').createPortal : null;

// ─── Column API ───────────────────────────────────────────────────────────────

export interface AdminTableColumn {
  key: string;
  label: string;
  /** Minimum pixel width; defaults to 120 */
  minWidth?: number;
  numeric?: boolean;
  /** Custom cell renderer; receives the Location row */
  renderCell?: (loc: Location) => React.ReactNode;
  /** Value extractor for default text rendering */
  getValue?: (loc: Location) => string | number | null | undefined;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LocationAdminTableProps {
  data: Location[];
  columns: AdminTableColumn[];
  sortKey?: string;
  sortDirection?: 'asc' | 'desc' | 'default';
  onSortChange?: (sortKey: string, direction: 'asc' | 'desc' | 'default') => void;

  // Row selection
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;

  // Navigation
  onRowPress: (loc: Location) => void;

  // Status actions
  onActivate: (loc: Location) => void;
  onDeactivate: (loc: Location) => void;
  onRevert: (loc: Location) => void;
  onIgnore: (loc: Location) => void;

  // Grouping: array of Location keys to group by (e.g. ['region', 'market'])
  groupBy?: string[];
  /** Given a location and group key, return the group label */
  getGroupValue?: (loc: Location, key: string) => string;
  /** When true, expand every group; when false, collapse every group */
  allExpanded?: boolean;

  loading?: boolean;
  footerText?: string;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

// ─── Status helpers ───────────────────────────────────────────────────────────

type StatusVariant = 'active' | 'pending' | 'removal' | 'disabled';

function statusVariant(status: string): StatusVariant {
  if (status === 'Active') return 'active';
  if (status === 'Pending Activation') return 'pending';
  if (status === 'Marked for Removal') return 'removal';
  return 'disabled';
}

const STATUS_PILL_COLORS: Record<StatusVariant, { bg: string; text: string }> = {
  active:   { bg: '#DDF2E1', text: '#176B2C' },
  pending:  { bg: '#FFF0CC', text: '#8A5A00' },
  removal:  { bg: '#FFE2DA', text: '#9C2300' },
  disabled: { bg: '#ECEFF3', text: '#5B6470' },
};

const STATUS_PILL_COLORS_DARK: Record<StatusVariant, { bg: string; text: string }> = {
  active:   { bg: 'rgba(56,182,56,0.22)',   text: '#8FEBA8' },
  pending:  { bg: 'rgba(217,150,0,0.22)',   text: '#FFD166' },
  removal:  { bg: 'rgba(220,80,50,0.22)',   text: '#FF9980' },
  disabled: { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4' },
};

const SHORT_STATUS: Record<string, string> = {
  'Active': 'Active',
  'Pending Activation': 'Pending',
  'Marked for Removal': 'Removal',
  'Disabled': 'Disabled',
};

const STICKY_LEFT = {
  select: 0,
  name: 42,
  id: 42 + 200,
  sap: 42 + 200 + 110,
};

const TooltipOverlay: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  if (Platform.OS !== 'web' || !visible || !position || !text || !createPortal) return null;

  const win: any = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 14,
        top: position.y - 10,
        maxWidth: 360,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 6,
          backgroundColor: '#1C1C1C',
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 18 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

const TruncatedTextCell: React.FC<{
  text: string;
  style?: any;
  numberOfLines?: number;
}> = ({ text, style, numberOfLines = 1 }) => {
  const [hovered, setHovered] = useState<{ text: string; x: number; y: number } | null>(null);
  const shouldShowTooltip = Platform.OS === 'web' && !!text && text !== '—';

  const webHandlers: any = Platform.OS === 'web' && shouldShowTooltip
    ? {
        onMouseEnter: (e: any) => {
          const rect = e?.currentTarget?.getBoundingClientRect?.();
          const x = rect?.left ?? e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
          const y = rect?.bottom ?? e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
          setHovered({ text, x, y });
        },
        onMouseMove: (e: any) => {
          const rect = e?.currentTarget?.getBoundingClientRect?.();
          const x = rect?.left ?? e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
          const y = rect?.bottom ?? e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
          setHovered(prev => (prev ? { ...prev, x, y } : null));
        },
        onMouseLeave: () => setHovered(null),
      }
    : {};

  return (
    <View style={{ width: '100%' }} {...webHandlers}>
      <TooltipOverlay visible={hovered !== null} position={hovered} text={hovered?.text ?? ''} />
      <Text style={style} numberOfLines={numberOfLines} {...webHandlers}>
        {text || '—'}
      </Text>
    </View>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** .lm-table-status pill */
const StatusPill: React.FC<{ status: string; isDark?: boolean }> = ({ status, isDark }) => {
  const v = statusVariant(status);
  const { bg, text } = (isDark ? STATUS_PILL_COLORS_DARK : STATUS_PILL_COLORS)[v];
  return (
    <View style={[styles.statusPill, { backgroundColor: bg }]}>
      <Text style={[styles.statusPillTxt, { color: text }]}>
        {SHORT_STATUS[status] ?? status}
      </Text>
    </View>
  );
};

const SortIcon: React.FC<{ active: boolean; direction?: 'asc' | 'desc' | 'default' }> = ({ active, direction }) => {
  const iconName = active && direction === 'desc'
    ? 'keyboard-arrow-down'
    : active && direction === 'asc'
      ? 'keyboard-arrow-up'
      : 'unfold-more';

  return (
    <View style={[styles.sortIconWrap, !active && styles.sortIconInactive]}>
      <MaterialIcons
        name={iconName}
        size={14}
        color={active ? AdminColors.blue700 : AdminColors.textSecondary}
      />
    </View>
  );
};

const ChevronRightIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <Svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    style={open ? { transform: [{ rotate: '90deg' }] } : undefined}
  >
    <Path d="M9 6l6 6-6 6V6z" fill={AdminColors.blue800} />
  </Svg>
);
const ChevronDownSmall = () => (
  <Svg width="13" height="13" viewBox="0 0 24 24">
    <Path d="M7 10l5 5 5-5H7z" fill="currentColor" />
  </Svg>
);
const CheckIcon = () => (
  <Svg width="9" height="7" viewBox="0 0 10 8" fill="none">
    <Path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);
const MinusIcon = () => (
  <Svg width="9" height="9" viewBox="0 0 10 10" fill="none">
    <Path d="M2 5h6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
  </Svg>
);

// ─── Inline Checkbox ──────────────────────────────────────────────────────────

interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onPress: () => void;
  label?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({ checked, indeterminate, onPress, label }) => (
  <TouchableOpacity
    onPress={onPress}
    style={styles.checkboxWrap}
    activeOpacity={0.7}
    accessibilityRole="checkbox"
    accessibilityLabel={label}
  >
    <View style={[
      styles.checkbox,
      (checked || indeterminate) && styles.checkboxFilled,
    ]}>
      {indeterminate && !checked ? <MinusIcon /> : checked ? <CheckIcon /> : null}
    </View>
  </TouchableOpacity>
);

// ─── Status action dropdown ───────────────────────────────────────────────────

interface StatusCellProps {
  loc: Location;
  onActivate: () => void;
  onDeactivate: () => void;
  onRevert: () => void;
  onIgnore: () => void;
  isDark?: boolean;
}

const StatusCell: React.FC<StatusCellProps> = ({
  loc, onActivate, onDeactivate, onRevert, onIgnore, isDark,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef<View>(null);

  const isInteractive =
    loc.status === 'Pending Activation' || loc.status === 'Marked for Removal';

  type MenuItem = { label: string; dot: string; action: () => void };

  const items: MenuItem[] =
    loc.status === 'Pending Activation'
      ? [
          { label: 'Activate',   dot: '#176B2C', action: () => { setOpen(false); onActivate(); } },
          { label: 'Ignore',     dot: '#7A7A7A', action: () => { setOpen(false); onIgnore(); } },
        ]
      : [
          { label: 'Deactivate',      dot: '#C12C01', action: () => { setOpen(false); onDeactivate(); } },
          { label: 'Revert to Active', dot: '#176B2C', action: () => { setOpen(false); onRevert(); } },
          { label: 'Ignore',           dot: '#7A7A7A', action: () => { setOpen(false); onIgnore(); } },
        ];

  const handleOpen = () => {
    if (!isInteractive) return;
    (btnRef.current as any)?.measureInWindow?.(
      (x: number, y: number, _w: number, h: number) => {
        setMenuPos({ top: y + h + 2, left: x });
        setOpen(true);
      }
    );
  };

  return (
    <View style={styles.statusWrap}>
      {/* .lm-status-action */}
      <TouchableOpacity
        ref={btnRef as any}
        onPress={handleOpen}
        activeOpacity={isInteractive ? 0.7 : 1}
        style={styles.statusAction}
        disabled={!isInteractive}
      >
        <StatusPill status={loc.status} isDark={isDark} />
        {isInteractive && (
          <Svg width="13" height="13" viewBox="0 0 24 24" style={{ marginLeft: 2 }}>
            <Path d="M7 10l5 5 5-5H7z" fill={STATUS_PILL_COLORS[statusVariant(loc.status)].text} />
          </Svg>
        )}
      </TouchableOpacity>

      {/* .lm-status-menu */}
      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={() => setOpen(false)}
      >
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={() => setOpen(false)}
        />
        <View style={[styles.statusMenu, { top: menuPos.top, left: menuPos.left }]}>
          {items.map((item, i) => (
            <React.Fragment key={item.label}>
              {i === items.length - 1 && items.length > 2 && (
                <View style={styles.statusMenuDivider} />
              )}
              <TouchableOpacity
                style={styles.statusMenuItem}
                onPress={item.action}
                activeOpacity={0.7}
              >
                <View style={[styles.statusDot, { backgroundColor: item.dot }]} />
                <Text style={styles.statusMenuLabel}>{item.label}</Text>
              </TouchableOpacity>
            </React.Fragment>
          ))}
        </View>
      </Modal>
    </View>
  );
};

// ─── Group tree helpers ───────────────────────────────────────────────────────

interface GroupNode {
  id: string;
  label: string;
  depth: number;
  count: number;
  totalCount: number;
  rows: Location[];
}

type FlatItem =
  | { kind: 'group'; node: GroupNode }
  | { kind: 'row';   loc: Location };

const compareGroupLabels = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

function buildFlatList(
  rows: Location[],
  groupKeys: string[],
  getVal: (loc: Location, key: string) => string,
  expanded: Set<string>,
  depth: number,
  parentId: string,
  totalCount: number,
): FlatItem[] {
  // Preserve incoming row order at the leaf level (table sort order from parent).
  if (!groupKeys.length) return rows.map(loc => ({ kind: 'row', loc }));

  const [key, ...rest] = groupKeys;
  const groups = new Map<string, Location[]>();
  rows.forEach(loc => {
    const v = getVal(loc, key) || 'Unassigned';
    if (!groups.has(v)) groups.set(v, []);
    groups.get(v)!.push(loc);
  });

  const result: FlatItem[] = [];
  [...groups.entries()]
    .sort((a, b) => compareGroupLabels(a[0], b[0]))
    .forEach(([label, children]) => {
      const id = parentId ? `${parentId}|${label}` : label;
      const node: GroupNode = { id, label, depth, count: children.length, totalCount, rows: children };
      result.push({ kind: 'group', node });
      if (expanded.has(id)) {
        result.push(...buildFlatList(children, rest, getVal, expanded, depth + 1, id, totalCount));
      }
    });
  return result;
}

function collectGroupIds(
  rows: Location[],
  groupKeys: string[],
  getVal: (loc: Location, key: string) => string,
  parentId = '',
): string[] {
  if (!groupKeys.length) return [];

  const [key, ...rest] = groupKeys;
  const groups = new Map<string, Location[]>();
  rows.forEach(loc => {
    const v = getVal(loc, key) || 'Unassigned';
    if (!groups.has(v)) groups.set(v, []);
    groups.get(v)!.push(loc);
  });

  const ids: string[] = [];
  [...groups.entries()]
    .sort((a, b) => compareGroupLabels(a[0], b[0]))
    .forEach(([label, children]) => {
      const id = parentId ? `${parentId}|${label}` : label;
      ids.push(id, ...collectGroupIds(children, rest, getVal, id));
    });

  return ids;
}

// ─── Default cell renderer ────────────────────────────────────────────────────

function renderDefaultCell(col: AdminTableColumn, loc: Location): React.ReactNode {
  if (col.renderCell) return col.renderCell(loc);
  const val = col.getValue ? col.getValue(loc) : (loc as any)[col.key];
  const text = val === null || val === undefined || val === '' ? '—' : String(val);
  return (
    <TruncatedTextCell
      text={text}
      style={[styles.cellTxt, col.numeric && styles.cellTxtNumeric]}
      numberOfLines={1}
    />
  );
}

// ─── Header row ───────────────────────────────────────────────────────────────

interface HeaderProps {
  columns: AdminTableColumn[];
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc' | 'default';
  onSortChange?: (sortKey: string, direction: 'asc' | 'desc' | 'default') => void;
  interColumnGap?: number;
}

const getNextSortDirection = (isActive: boolean, current?: 'asc' | 'desc' | 'default') => {
  if (!isActive) {
    return 'asc' as const;
  }

  if (current === 'asc') return 'desc' as const;
  if (current === 'desc') return 'default' as const;
  return 'asc' as const;
};

const HeaderRow: React.FC<HeaderProps> = ({
  columns, allSelected, someSelected, onToggleAll, sortKey, sortDirection, onSortChange, interColumnGap = 0,
}) => (
  <View style={styles.headerRow}>
    {/* select-col */}
    <View style={[styles.thSelect, styles.stickySelectCell, interColumnGap > 0 && { marginRight: interColumnGap }]}>
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && someSelected}
        onPress={onToggleAll}
        label="Select all"
      />
    </View>
    {/* Location */}
    <TouchableOpacity
      style={[styles.th, styles.sortableTh, styles.stickyNameCell, { width: 200 }, interColumnGap > 0 && { marginRight: interColumnGap }]}
      onPress={() => onSortChange?.('name', getNextSortDirection(sortKey === 'name', sortDirection))}
      activeOpacity={0.7}
    >
      <View style={styles.headerContent}>
        <Text style={styles.thTxt}>Location Name</Text>
        <SortIcon active={sortKey === 'name'} direction={sortDirection} />
      </View>
    </TouchableOpacity>
    {/* Location ID */}
    <TouchableOpacity
      style={[styles.th, styles.sortableTh, styles.stickyIdCell, { width: 110 }, interColumnGap > 0 && { marginRight: interColumnGap }]}
      onPress={() => onSortChange?.('id', getNextSortDirection(sortKey === 'id', sortDirection))}
      activeOpacity={0.7}
    >
      <View style={styles.headerContent}>
        <Text style={styles.thTxt}>Location ID</Text>
        <SortIcon active={sortKey === 'id'} direction={sortDirection} />
      </View>
    </TouchableOpacity>
    {/* SAP ID */}
    <TouchableOpacity
      style={[styles.th, styles.sortableTh, styles.stickySapCell, { width: 80 }, interColumnGap > 0 && { marginRight: interColumnGap }]}
      onPress={() => onSortChange?.('sap', getNextSortDirection(sortKey === 'sap', sortDirection))}
      activeOpacity={0.7}
    >
      <View style={styles.headerContent}>
        <Text style={styles.thTxt}>SAP ID</Text>
        <SortIcon active={sortKey === 'sap'} direction={sortDirection} />
      </View>
    </TouchableOpacity>
    {/* Dynamic */}
    {columns.map(col => (
      <TouchableOpacity
        key={col.key}
        style={[
          styles.th,
          styles.sortableTh,
          styles.dynamicCell,
          { width: col.minWidth ?? 120 },
          interColumnGap > 0 && { marginRight: interColumnGap },
          col.numeric && styles.thNumeric,
        ]}
        onPress={() => onSortChange?.(col.key, getNextSortDirection(sortKey === col.key, sortDirection))}
        activeOpacity={0.7}
      >
        <View style={styles.headerContent}>
          <Text style={styles.thTxt}>{col.label}</Text>
          <SortIcon active={sortKey === col.key} direction={sortDirection} />
        </View>
      </TouchableOpacity>
    ))}
    {/* Status */}
    <TouchableOpacity
      style={[styles.th, styles.sortableTh, { width: 130 }, interColumnGap > 0 && { marginRight: interColumnGap }]}
      onPress={() => onSortChange?.('status', getNextSortDirection(sortKey === 'status', sortDirection))}
      activeOpacity={0.7}
    >
      <View style={styles.headerContent}>
        <Text style={styles.thTxt}>Status</Text>
        <SortIcon active={sortKey === 'status'} direction={sortDirection} />
      </View>
    </TouchableOpacity>
  </View>
);

// ─── Location row ─────────────────────────────────────────────────────────────

interface RowProps {
  loc: Location;
  columns: AdminTableColumn[];
  selected: boolean;
  onToggle: () => void;
  onPress: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onRevert: () => void;
  onIgnore: () => void;
  interColumnGap?: number;
  isDark?: boolean;
}

const LocationRow: React.FC<RowProps> = ({
  loc, columns, selected,
  onToggle, onPress,
  onActivate, onDeactivate, onRevert, onIgnore,
  interColumnGap = 0,
  isDark = false,
}) => {
  const [hovered, setHovered] = useState(false);

  const rowBg = selected
    ? (isDark ? '#1B2A3D' : '#EAF4FF')
    : hovered
    ? (isDark ? '#1a1a1a' : AdminColors.bgHover)
    : (isDark ? '#000000' : '#fff');

  const hoverProps = Platform.OS === 'web'
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};

  return (
    <TouchableOpacity
      style={[styles.dataRow, { backgroundColor: rowBg }]}
      onPress={onPress}
      activeOpacity={0.85}
      {...(hoverProps as any)}
    >
      {/* select-col */}
      <View style={[styles.tdSelect, styles.stickySelectCell, { backgroundColor: rowBg }, interColumnGap > 0 && { marginRight: interColumnGap }]}>
        <Checkbox checked={selected} onPress={onToggle} label={`Select ${loc.name}`} />
      </View>

      {/* Location name + ID sub */}
      <View style={[styles.td, styles.stickyNameCell, { width: 200, backgroundColor: rowBg }, interColumnGap > 0 && { marginRight: interColumnGap }]}>
        <TouchableOpacity
          onPress={onPress}
          activeOpacity={0.7}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        >
          <TruncatedTextCell text={loc.name} style={[styles.locationName, isDark && { color: '#3D8BFF' }]} numberOfLines={1} />
        </TouchableOpacity>
      </View>

      {/* Location ID */}
      <View style={[styles.td, styles.stickyIdCell, { width: 110, backgroundColor: rowBg }, interColumnGap > 0 && { marginRight: interColumnGap }]}>
        <TruncatedTextCell text={loc.id || '—'} style={[styles.cellTxt, isDark && { color: '#F2F2F2' }]} numberOfLines={1} />
      </View>

      {/* SAP ID */}
      <View style={[styles.td, styles.stickySapCell, { width: 80, backgroundColor: rowBg }, interColumnGap > 0 && { marginRight: interColumnGap }]}>
        <TruncatedTextCell text={loc.sap || '—'} style={[styles.cellTxt, isDark && { color: '#F2F2F2' }]} numberOfLines={1} />
      </View>

      {/* Dynamic columns */}
      {columns.map(col => (
        <View
          key={col.key}
          style={[
            styles.td,
            styles.dynamicCell,
            { width: col.minWidth ?? 120 },
            interColumnGap > 0 && { marginRight: interColumnGap },
            col.numeric && { alignItems: 'flex-end' },
          ]}
        >
          {renderDefaultCell(col, loc)}
        </View>
      ))}

      {/* Status */}
      <View style={[styles.td, { width: 130 }]}>
        <StatusCell
          loc={loc}
          onActivate={onActivate}
          onDeactivate={onDeactivate}
          onRevert={onRevert}
          onIgnore={onIgnore}
          isDark={isDark}
        />
      </View>
    </TouchableOpacity>
  );
};

// ─── Group row ────────────────────────────────────────────────────────────────

interface GroupRowProps {
  node: GroupNode;
  columnCount: number;
  expanded: boolean;
  onToggle: () => void;
  isDark?: boolean;
}

const GroupRow: React.FC<GroupRowProps> = ({ node, columnCount, expanded, onToggle, isDark = false }) => {
  const bg = isDark
    ? (node.depth === 0 ? '#2B2B2B' : '#232323')
    : (node.depth === 0 ? AdminColors.blue25 : '#F7FAFE');
  const paddingLeft = 12 + node.depth * 20;
  const pct = node.totalCount > 0 ? Math.round(node.count / node.totalCount * 100) : 0;

  return (
    <TouchableOpacity
      style={[styles.groupRow, { backgroundColor: bg }, isDark && { borderBottomColor: '#3A3A3A' }]}
      onPress={onToggle}
      activeOpacity={0.7}
    >
      <View style={[styles.groupBtn, { paddingLeft }]}>
        <ChevronRightIcon open={expanded} />
        <Text style={styles.groupBtnTxt} numberOfLines={1}>{node.label}</Text>
        <View style={styles.groupTotal}>
          <Text style={styles.groupTotalCount}>
            {node.count} location{node.count !== 1 ? 's' : ''}
          </Text>
          <Text style={styles.groupTotalPct}>
            ({pct}% of all listed)
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// ─── Empty / loading states ───────────────────────────────────────────────────

const EmptyState: React.FC<{ colCount: number }> = () => (
  <View style={styles.emptyState}>
    <Text style={styles.emptyTxt}>No locations match the current filters and search.</Text>
  </View>
);

const LoadingState: React.FC = () => (
  <View style={styles.loadingState}>
    <ActivityIndicator size="small" color={AdminColors.blue700} />
    <Text style={styles.loadingTxt}>Loading locations…</Text>
  </View>
);

// ─── Main component ───────────────────────────────────────────────────────────

const LocationAdminTable: React.FC<LocationAdminTableProps> = ({
  data,
  columns,
  sortKey,
  sortDirection,
  onSortChange,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onRowPress,
  onActivate,
  onDeactivate,
  onRevert,
  onIgnore,
  groupBy = [],
  getGroupValue,
  allExpanded,
  loading,
  footerText,
  currentPage = 0,
  totalPages = 0,
  onPageChange,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  const headerScrollRef = React.useRef<ScrollView>(null);
  const bodyScrollRef = React.useRef<ScrollView>(null);
  const footerScrollRef = React.useRef<ScrollView>(null);
  const syncingScrollRef = React.useRef(false);

  const defaultGetVal = useCallback(
    (loc: Location, key: string): string => (loc as any)[key] ?? '',
    []
  );
  const getVal = getGroupValue ?? defaultGetVal;

  const handleSortChange = useCallback((sortKey: string, direction: 'asc' | 'desc' | 'default') => {
    onSortChange?.(sortKey, direction);
  }, [onSortChange]);

  const allGroupIds = useMemo(() => {
    if (!data.length || !groupBy.length) return [];
    return collectGroupIds(data, groupBy, getVal);
  }, [data, groupBy, getVal]);

  const flatItems = useMemo<FlatItem[]>(() => {
    if (!data.length) return [];
    if (!groupBy.length) return data.map(loc => ({ kind: 'row', loc }));
    return buildFlatList(data, groupBy, getVal, expanded, 0, '', data.length);
  }, [data, groupBy, getVal, expanded]);

  useEffect(() => {
    if (!groupBy.length) {
      setExpanded(new Set());
      return;
    }

    if (allExpanded == null) return;
    setExpanded(allExpanded ? new Set(allGroupIds) : new Set());
  }, [allExpanded, allGroupIds, groupBy.length]);

  const allSelected = data.length > 0 && data.every(l => selectedIds.has(l.id));
  const someSelected = !allSelected && data.some(l => selectedIds.has(l.id));

  const toggleGroup = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Total min-width: select(42) + location(200) + locId(110) + sap(80) + dynamic cols + status(130)
  const fixedWidth = 42 + 200 + 110 + 80 + 130;
  const dynamicWidth = columns.reduce((sum, c) => sum + (c.minWidth ?? 120), 0);
  const tableMinWidth = fixedWidth + dynamicWidth;
  const visibleCellCount = columns.length + 5;
  const interColumnGap = tableViewportWidth > tableMinWidth && visibleCellCount > 1
    ? (tableViewportWidth - tableMinWidth) / visibleCellCount
    : 0;
  const tableContentWidth = tableMinWidth + (interColumnGap * visibleCellCount);

  const defaultFooter = groupBy.length
    ? `Grouped ${data.length} location${data.length !== 1 ? 's' : ''} by ${groupBy.join(' › ')}`
    : `Showing ${Math.min(data.length, 500)} of ${data.length} location${data.length !== 1 ? 's' : ''}`;

  const hasPagination = Boolean(onPageChange && totalPages > 1);
  const activePage = Math.min(Math.max(currentPage, 0), Math.max(totalPages - 1, 0));
  const canGoPrevious = hasPagination && activePage > 0;
  const canGoNext = hasPagination && activePage < totalPages - 1;

  const syncHorizontalScroll = useCallback((source: 'header' | 'body' | 'footer', x: number) => {
    if (syncingScrollRef.current) return;

    syncingScrollRef.current = true;
    const refs = [headerScrollRef, bodyScrollRef, footerScrollRef];
    refs.forEach(ref => {
      if (ref !== (source === 'header' ? headerScrollRef : source === 'body' ? bodyScrollRef : footerScrollRef)) {
        ref.current?.scrollTo({ x, animated: false });
      }
    });

    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  return (
    <View
      style={styles.root}
      onLayout={event => setTableViewportWidth(event.nativeEvent.layout.width)}
    >
      {/* Sticky header — always visible */}
      <View
        style={[
          styles.headerWrap,
          Platform.OS === 'web' ? ({ position: 'sticky', top: 0, zIndex: 5 } as any) : {},
        ]}
      >
        <ScrollView
          ref={headerScrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ minWidth: tableContentWidth, width: '100%' }}
          scrollEnabled={tableMinWidth > tableViewportWidth}
          scrollEventThrottle={16}
          onScroll={event => syncHorizontalScroll('header', event.nativeEvent.contentOffset.x)}
        >
          <HeaderRow
            columns={columns}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={onToggleAll}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            interColumnGap={interColumnGap}
          />
        </ScrollView>
      </View>

      {/* Vertically scrollable body */}
      <ScrollView
        style={[styles.vScroll, isDark && { backgroundColor: '#000000' }]}
        showsVerticalScrollIndicator={true}
        scrollEnabled={true}
      >
        {/* Horizontally scrollable rows */}
        <ScrollView
          ref={bodyScrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[{ minWidth: tableContentWidth, width: '100%' }, isDark && { backgroundColor: '#000000' }]}
          scrollEnabled={tableMinWidth > tableViewportWidth}
          scrollEventThrottle={16}
          onScroll={event => syncHorizontalScroll('body', event.nativeEvent.contentOffset.x)}
        >
          <View style={[{ minWidth: tableContentWidth, width: '100%' }, isDark && { backgroundColor: '#000000' }]}>
            {/* Body */}
            {loading ? (
              <LoadingState />
            ) : !flatItems.length ? (
              <EmptyState colCount={columns.length} />
            ) : (
              flatItems.map((item, idx) => {
                if (item.kind === 'group') {
                  const { node } = item;
                  return (
                    <GroupRow
                      key={`group-${node.id}`}
                      node={node}
                      columnCount={columns.length}
                      expanded={expanded.has(node.id)}
                      onToggle={() => toggleGroup(node.id)}
                      isDark={isDark}
                    />
                  );
                }
                const { loc } = item;
                return (
                  <LocationRow
                      key={`row-${loc.id}-${idx}`}
                      loc={loc}
                      columns={columns}
                      selected={selectedIds.has(loc.id)}
                      onToggle={() => onToggleRow(loc.id)}
                      onPress={() => onRowPress(loc)}
                      onActivate={() => onActivate(loc)}
                      onDeactivate={() => onDeactivate(loc)}
                      onRevert={() => onRevert(loc)}
                      onIgnore={() => onIgnore(loc)}
                      interColumnGap={interColumnGap}
                      isDark={isDark}
                    />
                );
              })
            )}
          </View>
        </ScrollView>
      </ScrollView>

      {/* ── .lm-table-footer ── */}
      <View style={styles.footer}>
        <ScrollView
          ref={footerScrollRef}
          horizontal
          style={styles.footerScroll}
          contentContainerStyle={styles.footerScrollContent}
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          scrollEnabled={tableMinWidth > tableViewportWidth}
          scrollEventThrottle={16}
          onScroll={event => syncHorizontalScroll('footer', event.nativeEvent.contentOffset.x)}
        >
          <View style={{ width: tableContentWidth, height: 1 }} />
        </ScrollView>

        <View style={styles.footerMain}>
        <View style={styles.footerLeft}>
          <Text style={styles.footerTxt}>{footerText ?? defaultFooter}</Text>
          <Text style={styles.footerHint}>
            {groupBy.length
              ? 'Grouped rows preserve individual location access and status actions.'
              : 'Click a row to view location details.'}
          </Text>
        </View>

        {hasPagination && onPageChange && (
          <View style={styles.paginationWrap}>
            <TouchableOpacity
              style={[styles.pageButton, !canGoPrevious && styles.pageButtonDisabled]}
              onPress={() => onPageChange(activePage - 1)}
              disabled={!canGoPrevious}
              accessibilityLabel="Previous page"
            >
              <Text style={[styles.pageButtonText, !canGoPrevious && styles.pageButtonTextDisabled]}>
                Prev
              </Text>
            </TouchableOpacity>

            <View style={styles.pageIndicator}>
              <Text style={styles.pageIndicatorText}>
                Page {activePage + 1} of {totalPages}
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.pageButton, !canGoNext && styles.pageButtonDisabled]}
              onPress={() => onPageChange(activePage + 1)}
              disabled={!canGoNext}
              accessibilityLabel="Next page"
            >
              <Text style={[styles.pageButtonText, !canGoNext && styles.pageButtonTextDisabled]}>
                Next
              </Text>
            </TouchableOpacity>
          </View>
        )}
        </View>
      </View>
    </View>
  );
};

// ─── Styles — pixel-perfect match to HTML CSS ─────────────────────────────────

const styles = StyleSheet.create({
  root: {
    // .lm-admin-table-scroll: overflow auto, max-height 620px
    overflow: 'hidden' as any,
    backgroundColor: AdminColors.bgSurface,
    flex: 1,
  },

  hScroll: {
    maxHeight: 620,
  },

  vScroll: {
    maxHeight: 600,
    backgroundColor: AdminColors.bgSurface,
  },

  // ── Header ──

  headerWrap: {
    backgroundColor: AdminColors.bgSurface,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable, // #D7E5F8
  },

  headerRow: {
    // th: padding 10px 11px
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.bgSurface,
    width: '100%',
  },

  thSelect: {
    // select-col: width 42px, min-width 42px, text-align center
    width: 42,
    minWidth: 42,
    flexShrink: 0,
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
  },

  stickySelectCell: {
    position: 'sticky' as any,
    left: STICKY_LEFT.select,
    zIndex: 6,
    backgroundColor: AdminColors.bgSurface,
  },

  stickyNameCell: {
    position: 'sticky' as any,
    left: STICKY_LEFT.name,
    zIndex: 5,
    backgroundColor: AdminColors.bgSurface,
    overflow: 'hidden' as any,
  },

  stickyIdCell: {
    position: 'sticky' as any,
    left: STICKY_LEFT.id,
    zIndex: 4,
    backgroundColor: AdminColors.bgSurface,
    overflow: 'hidden' as any,
  },

  stickySapCell: {
    position: 'sticky' as any,
    left: STICKY_LEFT.sap,
    zIndex: 3,
    backgroundColor: AdminColors.bgSurface,
    overflow: 'hidden' as any,
  },

  th: {
    paddingVertical: 10,
    paddingHorizontal: 11,
    flexShrink: 0,
    justifyContent: 'flex-start',
    overflow: 'hidden' as any,
  },

  dynamicCell: {
    width: 120,
    flexShrink: 0,
  },

  sortableTh: {
    alignItems: 'flex-start',
    cursor: 'pointer' as any,
  },

  headerContent: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 6,
    overflow: 'hidden' as any,
  },

  thNumeric: {
    alignItems: 'flex-start',
  },

  thTxt: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
    flexShrink: 1,
    flexGrow: 1,
    minWidth: 0,
    flexWrap: 'wrap' as any,
    lineHeight: 14,
  },

  sortIconWrap: {
    width: 12,
    height: 12,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  },

  sortIconInactive: {
    opacity: 0.35,
  },

  // ── Data rows ──

  dataRow: {
    // tr.location-row: border-bottom 1px solid --nav-border-default
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    cursor: 'pointer' as any,
    width: '100%',
  },

  tdSelect: {
    width: 42,
    minWidth: 42,
    flexShrink: 0,
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 4,
  },

  td: {
    // td: padding 9px 11px, vertical-align middle
    paddingVertical: 9,
    paddingHorizontal: 11,
    flexShrink: 0,
    justifyContent: 'center',
  },

  // .lm-location-name
  locationName: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },

  // .lm-location-sub
  locationSub: {
    display: 'none' as any, // shown only in HTML; in RN we show it below
    fontSize: 10,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    marginTop: 2,
  },

  cellTxt: {
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },

  cellTxtNumeric: {
    textAlign: 'left',
    fontVariant: ['tabular-nums'] as any,
  },

  // ── Checkbox ──

  checkboxWrap: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },

  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.bgSurface,
  },

  checkboxFilled: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },

  // ── Status pill ──

  // .lm-table-status
  statusPill: {
    height: 22,
    borderRadius: 12,
    paddingHorizontal: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },

  statusPillTxt: {
    fontSize: 10,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },

  // .lm-status-wrap
  statusWrap: {
    position: 'relative',
  },

  statusAction: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  // .lm-status-menu
  statusMenu: {
    position: 'absolute',
    width: 170,
    padding: 5,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 7,
    shadowColor: '#002E69',
    shadowOffset: { width: 0, height: 7 },
    shadowOpacity: 0.18,
    shadowRadius: 22,
    elevation: 12,
    zIndex: 28,
  },

  statusMenuDivider: {
    height: 1,
    backgroundColor: AdminColors.gray25,
    marginVertical: 3,
    marginHorizontal: 4,
  },

  // .lm-status-menu button
  statusMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
  },

  // .lm-status-dot
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },

  statusMenuLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },

  // ── Icon button ──

  // ── Group rows ──

  // .lm-group-row td
  groupRow: {
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderTable, // #D7E5F8
    width: '100%',
  },

  // .lm-group-button
  groupBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingRight: 12,
  },

  groupBtnTxt: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    flex: 1,
  },

  // .lm-group-total
  groupTotal: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 5,
    marginLeft: 'auto' as any,
  },

  groupTotalCount: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },

  groupTotalPct: {
    fontSize: 10,
    fontWeight: '500',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },

  // ── Empty / loading ──

  emptyState: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },

  emptyTxt: {
    fontSize: 13,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    textAlign: 'center',
  },

  loadingState: {
    paddingVertical: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },

  loadingTxt: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },

  // ── Footer ──

  // .lm-table-footer
  footer: {
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingHorizontal: 15,
    paddingTop: 11,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
  },

  footerMain: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },

  footerLeft: {
    flex: 1,
    gap: 4,
  },

  footerTxt: {
    fontSize: 11,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },

  footerHint: {
    fontSize: 11,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },

  paginationWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },

  footerScroll: {
    width: '100%',
    height: 18,
    marginTop: 6,
  },

  footerScrollContent: {
    minHeight: 18,
  },

  pageButton: {
    minWidth: 56,
    height: 30,
    borderRadius: 6,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.blue700,
  },

  pageButtonDisabled: {
    backgroundColor: AdminColors.gray25,
  },

  pageButtonText: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.textOnDark,
    fontFamily: FontFamily.bold,
  },

  pageButtonTextDisabled: {
    color: AdminColors.textMuted,
  },

  pageIndicator: {
    minWidth: 110,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },

  pageIndicatorText: {
    fontSize: 11,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
});

export default LocationAdminTable;
